/**
 * Parent umbrella sections vs granular child modules.
 * Child view access requires an explicit grant on the child section — a parent
 * row in role_permissions (e.g. sales_pipeline) must NOT unlock children.
 */

export const SECTION_ALIASES = {
  // Merged duplicates (backend migration 411 / permissionCatalog MERGED_SECTIONS):
  // the old name's grants moved to the kept section, so a check on it reads that.
  customer_management: ['customer_management', 'customers'],
  customer_inventory: ['customer_inventory', 'customer_assets'],
  procurement: ['procurement', 'vendor_management'],
  inventory: ['inventory', 'inventory_management'],
  tickets: ['tickets', 'floor_tickets'],
  reports_access: ['reports_access', 'reports'],
  reports: ['reports', 'reports_access'],
  follow_ups: ['follow_ups', 'lead_follow_ups'],
  lead_follow_ups: ['follow_ups', 'lead_follow_ups'],
  sales_orders: ['sales_orders', 'sales_orders_doc'],
  sales_orders_replacement: ['sales_orders_replacement'],
  replacement_so_laptop_qc: ['replacement_so_laptop_qc'],
  so_laptop_qc: ['so_laptop_qc'],
  sales_order_cancel: ['sales_order_cancel'],
  sales_orders_doc: ['sales_orders', 'sales_orders_doc', 'sales_orders_sale', 'sales_orders_rental'],
  sales_orders_sale: ['sales_orders_sale', 'sales_orders_doc', 'sales_orders'],
  sales_orders_rental: ['sales_orders_rental', 'sales_orders_doc', 'sales_orders'],
  vendor_repair_dc: ['vendor_repair_dc', 'vendor_repair_dc_dispatch'],
};

/** Sidebar accordion / module umbrella → granular RBAC sections */
export const MODULE_CHILDREN = {
  sales_pipeline: [
    'sales_quotations',
    'sales_orders_doc',
    'sales_orders_sale',
    'sales_orders_rental',
    'sales_orders_replacement',
    'replacement_so_laptop_qc',
    'so_laptop_qc',
    'sales_order_cancel',
    'delivery_challans',
    'bluedart_awb_tracking',
    'return_dc',
    'delivery_register_management',
    'technician_bucket',
    'technicians_bucket_list',
    'delivery_technicians',
    'delivery_my_deliveries',
    'payment_records',
  ],
  floor_pipeline: ['floor_tickets', 'floor_ticket_assign', 'floor_ticket_config_edit', 'chip_level_repair', 'qc_management', 'dispatch_qc', 'dispatch_charger', 'dispatch_charger_reset', 'pending_inventory', 'diagnosis_failed'],
  vendor_management: ['vendor_repair_dc', 'vendor_repair_dc_dispatch'],
  inventory_management: ['customer_inventory', 'ttspl_history', 'inventory_master_data', 'inventory_vendor_master_data', 'inventory_return_master_data', 'inventory_asset_movement', 'ready_to_rent_location'],
  parts_management: [
    'parts_dashboard',
    'parts_inventory',
    'parts_approval',
    'dispatch_charger_warehouse',
    'parts_approval_export',
    'parts_history',
    'parts_procurement',
    'part_vendor_repair',
    'parts_discarded',
    'physical_dead_parts',
    'scrap_challans',
    'scrap_approval',
    'parts_detach',
    'parts_requests',
    'support_part_challan',
    'support_part_requests',
    'parts',
  ],
};

export const ALL_CHILD_SECTIONS = new Set(
  Object.values(MODULE_CHILDREN).flat()
);

export function sectionsToCheck(section) {
  return SECTION_ALIASES[section] || [section];
}

export function isChildModuleSection(section) {
  return ALL_CHILD_SECTIONS.has(section);
}

export function isParentModuleSection(section) {
  return Object.prototype.hasOwnProperty.call(MODULE_CHILDREN, section);
}

export function childSectionsForParent(parentSection) {
  return MODULE_CHILDREN[parentSection] || [];
}

/** Sections that grant entry to /inventory-management/* (umbrella route guard). */
export const INVENTORY_UMBRELLA_SECTIONS = [
  'inventory',
  'inventory_management',
  'inventory_master_data',
  'inventory_vendor_master_data',
  'inventory_return_master_data',
  'inventory_asset_movement',
  'parts',
  'parts_dashboard',
  'parts_inventory',
  'parts_approval',
  'dispatch_charger_warehouse',
  'parts_history',
  'parts_discarded',
  'physical_dead_parts',
  'scrap_challans',
  'scrap_approval',
  'parts_detach',
  'part_vendor_repair',
  'customer_inventory',
  'ttspl_history',
];

/** Sections that grant entry to /vendor-management/* (umbrella route guard). */
export const VENDOR_UMBRELLA_SECTIONS = [
  'vendor_management',
  'parts_procurement',
  'vendor_repair_dc',
  'vendor_repair_dc_dispatch',
];

/** @deprecated use VENDOR_UMBRELLA_SECTIONS */
export const PARTS_PROCUREMENT_UMBRELLA_SECTIONS = VENDOR_UMBRELLA_SECTIONS;

/** Sections that grant entry to /floor-pipeline/* (umbrella route guard). */
export const FLOOR_UMBRELLA_SECTIONS = [
  'floor_pipeline',
  'floor_tickets',
  'chip_level_repair',
  'qc_management',
  'dispatch_qc',
  'pending_inventory',
  'diagnosis_failed',
];
